import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  ShoppingCart as ShoppingCartIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const formatMoney = (value: number) => {
  const v = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(v);
};

const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { items, totalItems, totalPrice, shippingCost, grandTotal, updateQuantity, removeItem, clearCart } = useCartStore();

  const goTo = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 420 },
          maxWidth: '100vw',
          borderTopLeftRadius: { xs: 0, sm: 20 },
          borderBottomLeftRadius: { xs: 0, sm: 20 },
          overflow: 'hidden',
          boxShadow: '0 30px 90px rgba(17,24,39,0.22)',
          borderLeft: '1px solid rgba(0,0,0,0.06)',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={(t) => ({
            px: 2.25,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            backgroundColor: t.palette.action.hover,
            borderBottom: `1px solid ${t.palette.divider}`,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={(t) => ({
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: t.palette.primary.main,
                backgroundColor: t.palette.primary.main + '14',
                border: `1px solid ${t.palette.primary.main}2A`,
              })}
            >
              <ShoppingCartIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>Your Cart</Typography>
              <Typography variant="caption" color="text.secondary">
                {totalItems} item{totalItems === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.08)',
              background: 'rgba(255,255,255,0.7)',
              '&:hover': { background: 'rgba(255,255,255,0.9)' },
            }}
            aria-label="Close cart"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 2.25, py: 2 }}>
          {items.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Box
                sx={{
                  mx: 'auto',
                  width: 84,
                  height: 84,
                  borderRadius: 999,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  color: 'primary.main',
                  mb: 2,
                }}
              >
                <ShoppingCartIcon sx={{ fontSize: 34 }} />
              </Box>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Your cart is empty</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25 }}>
                Add something you love — it will appear here instantly.
              </Typography>
              <Button
                variant="contained"
                onClick={() => goTo('/all-products')}
                sx={(t) => ({
                  borderRadius: 999,
                  px: 3,
                  py: 1.15,
                  fontWeight: 800,
                  textTransform: 'none',
                  backgroundColor: t.palette.primary.main,
                  '&:hover': {
                    backgroundColor: t.palette.primary.dark,
                  },
                })}
              >
                Browse products
              </Button>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {items.map((item) => {
                const p: any = item.product as any;
                const title = String(p.title || p.name || 'Untitled');
                const image = Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : (p.image || '/placeholder-product.jpg');
                const unitPrice = Number.isFinite(p.price) ? Number(p.price) : 0;
                const perItemShipping = Number.isFinite(item.shippingCost as any) ? Number(item.shippingCost) : 0;
                const productId = p.id;
                const productPath = productId ? `/product/${productId}` : '';

                return (
                  <Box
                    key={item.id}
                    onClick={() => {
                      if (!productPath) return;
                      goTo(productPath);
                    }}
                    role={productPath ? 'button' : undefined}
                    tabIndex={productPath ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (!productPath) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goTo(productPath);
                      }
                    }}
                    sx={(t) => ({
                      borderRadius: 3,
                      border: `1px solid ${t.palette.divider}`,
                      backgroundColor: t.palette.background.paper,
                      overflow: 'hidden',
                      cursor: productPath ? 'pointer' : 'default',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                      '&:hover': productPath
                        ? {
                            borderColor: t.palette.primary.main,
                            boxShadow: '0 14px 40px rgba(17,24,39,0.10)',
                            transform: 'translateY(-1px)',
                          }
                        : undefined,
                    })}
                  >
                    <Box sx={{ display: 'flex', gap: 1.5, p: 1.5 }}>
                      <Avatar
                        variant="rounded"
                        src={image}
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2.5,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            lineHeight: 1.15,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {title}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                          {item.selectedSize && (
                            <Chip size="small" label={`Size: ${item.selectedSize}`} sx={{ borderRadius: 999 }} />
                          )}
                          {item.selectedColor && (
                            <Chip size="small" label={`Color: ${item.selectedColor}`} sx={{ borderRadius: 999 }} />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.1 }}>
                          <Typography sx={{ fontWeight: 900, color: 'text.primary' }}>{formatMoney(unitPrice)}</Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: 999,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                overflow: 'hidden',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateQuantity(item.id, item.quantity - 1);
                                }}
                                sx={{ width: 34, height: 34 }}
                                aria-label="Decrease quantity"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography sx={{ width: 28, textAlign: 'center', fontWeight: 900 }}>{item.quantity}</Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateQuantity(item.id, item.quantity + 1);
                                }}
                                sx={{ width: 34, height: 34 }}
                                aria-label="Increase quantity"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeItem(item.id);
                              }}
                              sx={(t) => ({
                                width: 34,
                                height: 34,
                                borderRadius: 2,
                                border: `1px solid ${t.palette.error.main}2A`,
                                color: t.palette.error.main,
                                backgroundColor: t.palette.error.main + '14',
                                '&:hover': {
                                  backgroundColor: t.palette.error.main + '1F',
                                },
                              })}
                              aria-label="Remove item"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Shipping: {formatMoney(perItemShipping)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Divider />

        <Box sx={{ px: 2.25, py: 2 }}>
          {items.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>{formatMoney(totalPrice)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Shipping (per item)</Typography>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>{formatMoney(shippingCost)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 900 }}>Total</Typography>
                <Typography sx={{ fontWeight: 900 }}>{formatMoney(grandTotal)}</Typography>
              </Box>
            </Box>
          )}

          <Stack spacing={1}>
            {items.length > 0 ? (
              <>
                <Button
                  variant="contained"
                  onClick={() => {
                    onClose();
                    navigate('/checkout', { state: { from: location.pathname + location.search } });
                  }}
                  startIcon={<LockIcon />}
                  sx={(t) => ({
                    borderRadius: 999,
                    py: 1.2,
                    fontWeight: 900,
                    textTransform: 'none',
                    backgroundColor: t.palette.primary.main,
                    boxShadow: '0 10px 26px rgba(17,24,39,0.14)',
                    '&:hover': {
                      backgroundColor: t.palette.primary.dark,
                      boxShadow: '0 12px 30px rgba(17,24,39,0.18)',
                    },
                  })}
                >
                  Checkout
                </Button>

                <Button
                  variant="text"
                  onClick={() => clearCart()}
                  sx={{
                    borderRadius: 999,
                    py: 0.9,
                    fontWeight: 800,
                    textTransform: 'none',
                    color: 'text.secondary',
                    '&:hover': { background: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  Clear cart
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 900,
                  textTransform: 'none',
                }}
              >
                Continue shopping
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CartDrawer;
